import { useMemo } from 'react';
import { CreateScheduleBlockForm } from '@/features/tournament-wizard/components/create-schedule-block';
import type { TimeSlotsByDate } from '@/features/tournament-wizard/components/create-schedule-block';
import { setRoundSchedule } from '@/api/tournament/tournamentRoundApi';
import { handleGlobalError } from '@/utils/error-handler';
import { toast } from '@/components/ui/use-toast';
import { getDayOfWeek } from '@/types/schedule.model';
import type { Club } from '@/types/tournament.model';
import type { TournamentRound, RoundScheduleSlot } from '@/types/tournament-round.model';

interface RoundScheduleEditorProps {
  tournamentId: string;
  round: TournamentRound;
  club: Club;
  matchDuration: number;
  sportUid?: string | null;
  onClose: () => void;
  onSaved: (updatedRound: TournamentRound) => void;
}

/**
 * Converts RoundScheduleSlot[] (the backend's flat format) into the TimeSlotsByDate
 * format CreateScheduleBlockForm expects: Record<date, Record<startTime, courtCount>>.
 */
function slotsToTimeSlotsByDate(slots: RoundScheduleSlot[]): TimeSlotsByDate {
  const byDate: TimeSlotsByDate = {};
  for (const slot of slots) {
    if (!byDate[slot.date]) byDate[slot.date] = {};
    byDate[slot.date][slot.startTime] = slot.courtsQuantity;
  }
  return byDate;
}

/**
 * Round schedule editor. Reuses CreateScheduleBlockForm hydrated with the
 * round's current schedule (singleWeekMode = no range selector). On save,
 * converts the form's ScheduleEntry[] into RoundScheduleSlot[] and calls setRoundSchedule.
 */
export function RoundScheduleEditor({
  tournamentId,
  round,
  club,
  matchDuration,
  sportUid,
  onClose,
  onSaved,
}: RoundScheduleEditorProps) {
  const initialSlots = useMemo(
    () => slotsToTimeSlotsByDate(round.schedule ?? []),
    [round.schedule],
  );

  return (
    <CreateScheduleBlockForm
      club={club}
      matchDuration={matchDuration}
      sportUid={sportUid}
      onClose={onClose}
      initialSlots={initialSlots}
      initialName={`Fecha ${round.roundNumber}`}
      singleWeekMode
      submitLabel="Guardar horarios"
      submittingLabel="Guardando..."
      onSubmitRequest={async (request) => {
        try {
          // ScheduleEntry[] (date, dayOfWeek, timeRange[]) → RoundScheduleSlot[] plano
          const slots: RoundScheduleSlot[] = request.schedule.flatMap((entry) =>
            entry.timeRange.map((tr) => ({
              date: entry.date,
              dayOfWeek:
                entry.dayOfWeek ?? getDayOfWeek(new Date(entry.date + 'T00:00:00')),
              startTime: tr.startTime,
              endTime: tr.endTime,
              qty: tr.qty,
              courtsQuantity: tr.courtsQuantity,
              clubId: club.id,
              clubName: club.name,
            })),
          );
          const updated = await setRoundSchedule(tournamentId, round.id, slots);
          onSaved(updated);
          toast({ title: 'Horarios de la jornada actualizados' });
        } catch (err) {
          handleGlobalError(err, 'Error', 'No se pudieron guardar los horarios de la jornada');
          throw err;
        }
      }}
    />
  );
}
